/* ============================================================
   dialog.js — het dialoogsysteem: laden en modals.

   Het origineel doet dit met customized built-in elements
   (`<button is="dialog-trigger" dialog-selector="#DrawerCart">`). Die bestaan
   niet in Safari, dus openen wij op `[data-dialog-trigger="#Id"]` en op
   `[data-a11y-dialog-hide]` binnen de dialoog zelf. De klassen, de
   `aria-hidden`-schakelaar en de `[data-dialog-element]`-haken zijn wél
   identiek: daar hangt alle CSS aan.

   Wat een dialoog hier moet doen om echt bruikbaar te zijn:
   - focus verplaatsen naar binnen en vasthouden zolang hij open is,
   - Escape en een klik op de overlay sluiten hem,
   - de focus gaat terug naar het element dat hem opende,
   - de body gaat op slot (`body.is-lock`) zodat de pagina er niet onder
     doorscrollt — die klasse staat al in main.css van het origineel.

   NIET het native <dialog>-element (04-09-2026, toegankelijkheid-stream).
   Nagekeken vóór er iets gewijzigd werd: deze laden zijn custom elements
   (`drawer-cart`, `dialog-element`, …) die met `aria-hidden` open en dicht
   gaan, en álle CSS hangt aan die tagnamen en aan `[role=document]`
   (thema.css r. 3283-3490). `showModal()` bestaat hier dus niet, en daarmee
   ook geen browsergedrag dat focus vasthoudt, Escape afvangt of de
   achtergrond op inert zet. Dat moet dit bestand zelf doen — vandaar dat het
   hieronder staat en niet "dubbelop" is.

   Wat de rol erbij vraagt: `role="dialog"` alléén maakt het voor een
   schermlezer slechter, niet beter. Bij de rol horen daarom in dezelfde
   beweging: `aria-modal="true"`, een toegankelijke naam (in de secties gezet
   met `aria-labelledby` naar de zichtbare kop, of `aria-label` als er geen kop
   is), en een achtergrond die op `inert` + `aria-hidden` gaat zolang de
   dialoog open staat.
   ============================================================ */
const FOCUSBAAR = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
const SLUITKNOP = '[data-a11y-dialog-hide], [data-dialog-element="close"]';

/* ---------- de achtergrond ----------------------------------------------
   `aria-modal="true"` vertelt een schermlezer dat de rest van de pagina niet
   meetelt, maar het doet niets voor het toetsenbord: zonder extra werk tabt
   een toetsenbordgebruiker gewoon de kop en de voet achter de lade in. `inert`
   doet dat wél — het haalt de tak uit de tabvolgorde én uit de
   toegankelijkheidsboom, en het verandert niets aan de opmaak.

   We zetten het per niveau vanaf de dialoog omhoog: alle broers van de
   dialoog, alle broers van zijn sectie, tot en met de kinderen van <body>.
   Zo hoeft er niets in layout/theme.liquid te veranderen en klopt het ook als
   er een dialoog bijkomt.

   Twee dingen om niet te breken:
   - een dialoog die al dicht is heeft zelf `aria-hidden="true"` en `display:
     none`; die slaan we over, anders zetten we hem bij het herstellen open.
   - staat er al een dialoog open (quickshop → lightbox), dan gaat de
     onderste op inert maar NIET op `aria-hidden`, want `.dialog[aria-hidden=
     true]` is in de CSS `display:none` en dan knippert de onderste lade weg. */
const stapel = [];
let inertDoorOns = [];
let verborgenDoorOns = [];

function herstelAchtergrond() {
  inertDoorOns.forEach((el) => el.removeAttribute('inert'));
  verborgenDoorOns.forEach((el) => el.removeAttribute('aria-hidden'));
  inertDoorOns = [];
  verborgenDoorOns = [];
}

function markeerAchtergrond(dialoogEl) {
  herstelAchtergrond();
  if (!dialoogEl) return;
  const overslaan = new Set(['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE', 'NOSCRIPT']);
  let knoop = dialoogEl;
  while (knoop && knoop !== document.body && knoop.parentElement) {
    for (const broer of knoop.parentElement.children) {
      if (broer === knoop || overslaan.has(broer.tagName)) continue;
      if (broer.hasAttribute('inert')) continue;                 // niet van ons: met rust laten
      if (broer.getAttribute('aria-hidden') === 'true') continue; // al verborgen (dichte dialoog)
      broer.setAttribute('inert', '');
      inertDoorOns.push(broer);
      const isOpenDialoog = broer.classList.contains('dialog') && broer.getAttribute('aria-hidden') === 'false';
      const bevatOpenDialoog = broer.querySelector('.dialog[aria-hidden="false"]');
      if (!isOpenDialoog && !bevatOpenDialoog) {
        broer.setAttribute('aria-hidden', 'true');
        verborgenDoorOns.push(broer);
      }
    }
    knoop = knoop.parentElement;
  }
}

class Dialoog {
  constructor(el) {
    this.el = el;
    this.opener = null;
    /* De rol staat in de Liquid; hier staat hij nog een keer voor het geval een
       dialoog van elders komt (Section Rendering API, quickshop). */
    if (!this.el.hasAttribute('role')) this.el.setAttribute('role', 'dialog');
    this.el.setAttribute('aria-modal', 'true');
    this.el.setAttribute('aria-hidden', 'true');
    this.el.hidden = false;              // zichtbaarheid loopt via aria-hidden, niet via [hidden]
    /* SLUITKNOPPEN VIA DELEGATIE (17-09-2026, Omnify).

       Hier werd bij het aanmaken aan elke sluitknop een eigen listener
       gehangen. cart.js vervangt na elke wijziging de hele binnenkant van
       `[role=document]` — inclusief de kop met het kruisje. Het nieuwe
       kruisje had dan geen listener meer: met een gevulde wagen ging de lade
       niet meer dicht via het kruisje (de overlay werkte nog, die valt buiten
       `[role=document]`). Met een lege wagen was er niets vervangen en werkte
       het wel.

       Nu luistert de dialoog zelf en kijkt hij bij elke klik of er op een
       sluitknop is geklikt. Dat werkt ook voor knoppen die later worden
       ingeladen. De tweede controle zorgt dat een sluitknop in een geneste
       dialoog alleen die geneste dialoog sluit. */
    this.el.addEventListener('click', (e) => {
      const knop = e.target.closest(SLUITKNOP);
      if (!knop || knop.closest('[role="dialog"]') !== this.el) return;
      this.sluit();
    });
    this.el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); this.sluit(); }
      if (e.key === 'Tab') this.houdFocus(e);
    });
  }

  get open() { return this.el.getAttribute('aria-hidden') === 'false'; }

  toon(opener) {
    if (this.open) return;
    this.opener = opener || document.activeElement;
    this.el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-lock');
    document.documentElement.style.setProperty('--scrollbar-width',
      String(window.innerWidth - document.documentElement.clientWidth));
    stapel.push(this.el);
    markeerAchtergrond(this.el);
    /* Focus naar binnen. Is er niets om te focussen (een lege lightbox, een
       quickshop die nog laadt), dan gaat hij naar de `[role=document]`-huls —
       die heeft daarvoor `tabindex="-1"`. Zonder dat blijft de focus op de
       knop in de nu-inerte kop staan en verliest de browser hem bij de
       volgende toetsaanslag. */
    const eerste = this.focusbaar()[0] || this.el.querySelector(FOCUSBAAR);
    (eerste || this.el.querySelector('[role="document"]') || this.el).focus({ preventScroll: true });
    this.el.dispatchEvent(new CustomEvent('dialoog:open', { bubbles: true }));
  }

  sluit() {
    if (!this.open) return;
    this.el.setAttribute('aria-hidden', 'true');
    const plek = stapel.lastIndexOf(this.el);
    if (plek > -1) stapel.splice(plek, 1);
    /* Eerst de achtergrond vrijgeven, dán terugfocussen: een element in een
       `inert`-tak neemt geen focus aan, dus andersom belandt de focus op
       <body> en werkt Escape daarna nergens meer. */
    markeerAchtergrond(stapel[stapel.length - 1] || null);
    if (!document.querySelector('.dialog[aria-hidden="false"]')) document.body.classList.remove('is-lock');
    this.opener?.focus({ preventScroll: true });
    this.el.dispatchEvent(new CustomEvent('dialoog:dicht', { bubbles: true }));
  }

  /* Wat er binnen deze dialoog echt te focussen valt: zichtbaar (`offsetParent`)
     en niet in een `inert`-tak. Dat laatste is er sinds de navigatielade zijn
     hoofdmenu op inert zet zodra een submenu eroverheen schuift; zonder deze
     filter denkt de focusval dat het eerste menu-item nog de eerste tabstop is
     en springt Shift+Tab naar een link die niemand kan zien. */
  focusbaar() {
    return [...this.el.querySelectorAll(FOCUSBAAR)]
      .filter((el) => el.offsetParent !== null && !el.closest('[inert]'));
  }

  houdFocus(e) {
    const items = this.focusbaar();
    if (!items.length) return;
    const eerste = items[0], laatste = items[items.length - 1];
    if (e.shiftKey && document.activeElement === eerste) { e.preventDefault(); laatste.focus(); }
    else if (!e.shiftKey && document.activeElement === laatste) { e.preventDefault(); eerste.focus(); }
  }
}

/* Elke dialoog is een custom element, want de CSS van het origineel stuurt op de
   tagnaam (drawer-cart, drawer-navigation). De logica zit in Dialoog. */
class DialoogElement extends HTMLElement {
  connectedCallback() {
    if (!this.dialoog) this.dialoog = new Dialoog(this);
  }
  show(opener) { this.dialoog?.toon(opener); }
  hide() { this.dialoog?.sluit(); }
}
/* `dialog-element` is de generieke naam die het origineel voor zijn modals
   gebruikt (quickshop, lightbox); de laden hebben een eigen tagnaam omdat de CSS
   daarop stuurt. */
for (const naam of ['drawer-cart', 'drawer-navigation', 'drawer-account', 'drawer-facets',
                    'modal-markets', 'dialog-element']) {
  if (!customElements.get(naam)) customElements.define(naam, class extends DialoogElement {});
}

document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-dialog-trigger]');
  if (trigger) {
    const doel = document.querySelector(trigger.getAttribute('data-dialog-trigger'));
    if (!doel) return;                  // geen dialoog op deze pagina: laat de link gewoon werken
    e.preventDefault();
    const dlg = doel.dialoog || (doel.dialoog = new Dialoog(doel));
    dlg.open ? dlg.sluit() : dlg.toon(trigger);
    return;
  }

  /* Elke link naar de winkelwagen opent de lade, niet de pagina. Het icoon in de
     kop draagt `data-dialog-trigger` en werd hierboven al afgevangen, maar een
     winkelwagenlink kan overal staan — in de voet, in een lege staat, in een
     tekstblok dat de klant zelf invult. Eén van die links die tóch naar /cart
     springt haalt de klant uit de flow die we net gebouwd hebben.

     De link blijft een echte link: zonder JavaScript, of als de lade op deze
     pagina niet bestaat, gaat hij gewoon naar /cart. Dat is de bedoeling.
     Een klik met cmd, ctrl of shift (nieuw tabblad) laten we met rust. */
  const cartlink = e.target.closest('a[href]');
  if (!cartlink || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
  if (cartlink.hasAttribute('download') || cartlink.target === '_blank') return;
  let pad;
  try { pad = new URL(cartlink.href, location.href).pathname; } catch (err) { return; }
  if (!/(^|\/)cart\/?$/.test(pad)) return;
  const lade = document.querySelector('#DrawerCart');
  if (!lade) return;                    // geen lade op deze pagina: gewoon naar /cart
  e.preventDefault();
  const dlg = lade.dialoog || (lade.dialoog = new Dialoog(lade));
  dlg.open ? dlg.sluit() : dlg.toon(cartlink);
});

/* Escape werkt op documentniveau en niet alleen binnen de dialoog zelf.
   Reden: de focus kan buiten de dialoog belanden zodra een onderdeel verdwijnt —
   in de navigatielade gebeurt dat als je uit een submenu terugkeert en de
   terugknop verborgen wordt. Dan valt de focus naar <body> en zou Escape niets
   meer doen; je zit dan zonder muis vast in een open lade. */
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  const open = [...document.querySelectorAll('.dialog[aria-hidden="false"]')].pop();
  open?.dialoog?.sluit();
});

window.Dialoog = Dialoog;
